import { isDarkMode } from '#lib/stores/dark-mode.js';
import { derived } from 'svelte/store';

export type InputStyleOptions = {
	width?: string;
	height?: string;
	rounded?: string;
	padding?: string;
	textColor?: string;
	textSize?: string;
	textAlign?: string;
	border?: string;
	bg?: string;
	placeholder?: string;
	disabled?: string;
};

export const glassDesignStyle: InputStyleOptions = {
	bg: ' bg-surface/30 dark:bg-surface-dark/30',
	placeholder: ' placeholder-surface/50 dark:placeholder-surface/50',
	border: ' border border-surface/30 dark:border-surface-dark/60 border-solid focus:border-secondary dark:focus:border-secondary-dark ',
	textColor: ' text-surface dark:text-surface/70',
	textSize: ' text-sm',
	disabled:
		' disabled:bg-surface/10 dark:disabled:bg-surface-dark/10 disabled:border-surface/20 dark:disabled:border-surface-dark/40 disabled:text-surface/40 dark:disabled:text-surface/30 disabled:placeholder-surface/20 dark:disabled:placeholder-surface/20 disabled:cursor-not-allowed'
};

export function cssInput(error: string, options: InputStyleOptions = {}) {
	const width = options.width ?? 'w-full max-w-full';
	const height = options.height ?? '';
	const rounded = options.rounded ?? 'rounded-md';
	const padding = options.padding ?? 'px-3 py-2';
	const textAlign = options.textAlign ?? '';
	const textColor = options.textColor ?? 'text-surface-on dark:text-surface-dark-on';
	const textSize = options.textSize ?? 'text-sm';
	const bg = options.bg ?? 'bg-surface dark:bg-surface-dark';
	const border =
		options.border ??
		'border border-border dark:border-border-dark border-solid focus:border-secondary dark:focus:border-secondary-dark ';
	const disabled =
		options.disabled ??
		'disabled:bg-disabled dark:disabled:bg-disabled-dark disabled:cursor-not-allowed disabled:text-disabled-on dark:disabled:text-disabled-dark-on';
	let css =
		height +
		' ' +
		padding +
		' ' +
		textAlign +
		' ' +
		width +
		' ' +
		rounded +
		' ' +
		bg +
		' ' +
		textSize +
		' focus:outline-hidden ' +
		disabled +
		' transition-colors duration-1000 ease-in-out';
	if (error) {
		css += ' border border-error placeholder-error';
	} else {
		css +=
			options.placeholder ??
			' placeholder-disabled-on/50 dark:placeholder-disabled-dark-on/50';
		css += ' ' + textColor + ' ' + border;
	}

	return css;
}

export function getRandomPartialColor() {
	const letters = '012345BCDEF';
	let color = '#';
	for (let i = 1; i <= 6; i++) {
		color += letters[Math.floor(Math.random() * letters.length)];
	}
	return color;
}

/**
 * Convert an RGB color from format '255,255,255' to hex format '#FFFFFF'.
 */
export function getHexColor(rgb: string) {
	const parts = rgb.split(',').map((part) => parseInt(part.trim(), 10));

	return rgbToHex(parts[0], parts[1], parts[2]);
}

/**
 * Return the contrast color based on the YIQ formula.
 * By default, it returns #000000 (black) for light colors and #FFFFFF (white) for dark colors.
 * Can be used with a custom dark and light color or classes.
 */
export function getContrastYIQ(
	hexColor: string,
	dark: string = '#000000',
	light: string = '#ffffff'
) {
	if (hexColor === '#f26c23') {
		// Reservatoo logo orange color should always be contrasted with white
		return light;
	}
	if (!hexColor || !hexColor.startsWith('#') || hexColor.length != 7) {
		return dark;
	}
	const r = parseInt(hexColor.substr(1, 2), 16);
	const g = parseInt(hexColor.substr(3, 2), 16);
	const b = parseInt(hexColor.substr(5, 2), 16);
	const yiq = (r * 299 + g * 587 + b * 114) / 1000;
	return yiq >= 128 ? dark : light;
}

/**
 * Return a contrasted enough color based on the background if needed
 * If the contrast is good, the color is returned as is.
 * If the contrast is not good, the color is returned as black or white based on the YIQ formula.
 */
export function getContrastOnBackground(
	hexColor: string,
	backgroundColor: string,
	dark: string = '#000000',
	light: string = '#ffffff'
) {
	// If the hexColor is not contrasted with the background, return the black or white color based on the YIQ formula
	if (getContrastYIQ(backgroundColor, dark, light) == getContrastYIQ(hexColor, dark, light)) {
		return getContrastYIQ(backgroundColor, dark, light);
	}

	// Or keep the color
	return hexColor;
}

export function getPrimaryColor(isWidget: boolean = false) {
	// get css primary color value
	const primaryColor = getComputedStyle(document.documentElement).getPropertyValue(
		isWidget ? '--color-widget-primary' : '--primary-color'
	);
	if (primaryColor.startsWith('#')) {
		return primaryColor;
	}
	return getHexColor(primaryColor);
}

export function getSecondaryColor() {
	// get css primary color value
	const color = getComputedStyle(document.documentElement).getPropertyValue('--color-secondary');
	if (color.startsWith('#')) {
		return color;
	}
	return getHexColor(color);
}

export function getColorWithOpacity(colorHex: string, opacity: number) {
	return colorHex + Math.round(opacity * 255).toString(16);
}

export function getHexColorFromTailwind(className: string) {
	// Create a div with the class name
	const element = document.createElement('div');
	element.className = className + '';
	document.body.appendChild(element);

	// Get the color value
	let colorValue = getComputedStyle(element).backgroundColor;

	document.body.removeChild(element);

	// Convert rgb to hex if needed
	if (colorValue.startsWith('rgb')) {
		colorValue = getHexColor(
			colorValue.replace('rgba', 'rgb').replace('rgb(', '').replace(')', '')
		);
	} else if (colorValue.startsWith('oklab') || colorValue.startsWith('oklch')) {
		const canvas = document.createElement('canvas');
		canvas.width = 1;
		canvas.height = 1;
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		if (ctx) {
			ctx.fillStyle = '#ffffff'; // Fallback background
			ctx.fillRect(0, 0, 1, 1);
			ctx.fillStyle = colorValue;
			ctx.fillRect(0, 0, 1, 1);
			const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
			colorValue = `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
		}
	}
	return colorValue;
}

export function changeColor(newColor: string, target: string) {
	document.documentElement.style.setProperty(`--color-${target}`, newColor);
	document.documentElement.style.setProperty(`--color-${target}-on`, getContrastYIQ(newColor));
}

export const uuidToColor = derived([isDarkMode], ([$isDarkMode]) => (uuid: string | undefined) => {
	return _uuidToColor(uuid, $isDarkMode);
});

function _uuidToColor(uuid: string | undefined, isDark: boolean): string {
	if (!uuid) {
		return '';
	}
	// Remove dashes from the UUID
	const cleanedUUID = uuid.replace(/-/g, '');

	// Convert the cleaned UUID to a numeric value by treating it as a hexadecimal number
	const numericValue = parseInt(cleanedUUID, 16);

	// Generate hue from the numeric value (between 0 and 360)
	const hue = numericValue % 360;

	// Fixed low saturation and high lightness for pastel colors
	const saturation = 50; // Pastel colors have low saturation
	const lightness = isDark ? 20 : 85; // High lightness for soft colors, depending on the theme

	const [r, g, b] = hslToRgb(hue, saturation, lightness);

	// Return the HSL color string
	return rgbToHex(r, g, b); //`hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

function hslToRgb(h: number, s: number, l: number) {
	s /= 100;
	l /= 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = s * Math.min(l, 1 - l);
	const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

	return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

// Convertir RGB in Hex
function rgbToHex(r: number, g: number, b: number) {
	return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
